import { useAuthStore } from '@/stores/auth.store';

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// 401 Refresh-token rotation state
// ---------------------------------------------------------------------------

/** Whether a refresh request is currently in-flight. */
let isRefreshing = false;

/**
 * Queue of callers waiting for the refresh to complete.
 * Each entry holds `resolve` (new token) or `reject` (refresh failed).
 */
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

/** Process all queued callers after a refresh attempt. */
function drainQueue(error: unknown, newToken?: string) {
  for (const entry of refreshQueue) {
    if (error) {
      entry.reject(error);
    } else {
      entry.resolve(newToken ?? '');
    }
  }
  refreshQueue = [];
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * On success, updates the auth store and returns the new access token.
 * On failure, logs the user out and redirects to /login.
 */
async function attemptTokenRefresh(): Promise<string> {
  const { refreshToken, setAuth } = useAuthStore.getState();

  if (!refreshToken) {
    handleSessionExpired();
    throw new Error('No refresh token available');
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Network error (offline, DNS, etc.)
    handleSessionExpired();
    throw new Error('Token refresh failed: network error');
  }

  if (!response.ok) {
    handleSessionExpired();
    throw new Error('Refresh token rejected');
  }

  let json: { success: boolean; data: { token: string; refreshToken: string; user?: { id: number; rut: string; email: string; nombre: string; tipoUsuarioId: number; ambitoIds: number[] } } };
  try {
    json = (await response.json()) as typeof json;
  } catch {
    handleSessionExpired();
    throw new Error('Token refresh failed: invalid response');
  }

  const newToken = json.data.token;
  const newRefreshToken = json.data.refreshToken;

  // Use fresh user from refresh response if available, otherwise keep current
  const freshUser = json.data.user ?? useAuthStore.getState().user;
  if (freshUser) {
    setAuth(newToken, newRefreshToken, freshUser);
  }

  return newToken;
}

/**
 * Clear auth state and redirect to login with returnUrl.
 * Shows a brief DOM-based toast so the user knows their session expired.
 */
function handleSessionExpired() {
  const { logout } = useAuthStore.getState();
  logout();

  showSessionExpiredToast();

  // Redirect to login with returnUrl (unless already on login page)
  const currentPath = window.location.pathname;
  if (currentPath !== '/login') {
    // Use replaceState + assign to pass state via URL param
    // (can't use react-router navigate from outside React tree)
    const returnUrl = encodeURIComponent(currentPath + window.location.search);
    window.location.replace(`/login?returnUrl=${returnUrl}`);
  }
}

/**
 * Lightweight DOM-based toast for session expiry.
 * Rendered outside React to work from the API layer.
 */
function showSessionExpiredToast() {
  // Guard: don't stack multiple toasts
  if (document.getElementById('sime-session-toast')) return;

  const toast = document.createElement('div');
  toast.id = 'sime-session-toast';
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');

  Object.assign(toast.style, {
    position: 'fixed',
    top: '24px',
    left: '50%',
    transform: 'translateX(-50%) translateY(-8px)',
    zIndex: '99999',
    padding: '14px 24px',
    borderRadius: '16px',
    background: 'var(--sime-surface, #fff)',
    color: 'var(--sime-danger, #dc2626)',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    border: '1px solid var(--sime-danger, #dc2626)',
    opacity: '0',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    pointerEvents: 'none' as const,
  });

  toast.textContent = 'Sesion expirada. Inicie sesion nuevamente.';
  document.body.appendChild(toast);

  // Animate in
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
  });

  // Auto-remove after 4s
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(-8px)';
    setTimeout(() => { toast.remove(); }, 300);
  }, 4000);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  /** Internal flag: set to true when retrying after a 401 refresh. */
  _isRetry?: boolean;
}

export async function apiClient<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { skipAuth, _isRetry, ...fetchOptions } = options;
  const token = useAuthStore.getState().token;

  // Only set Content-Type: application/json when there's a JSON body to send.
  // Fastify's JSON parser rejects empty bodies that declare application/json,
  // so bodyless requests (e.g. POST confirm endpoints) must omit the header.
  // FormData is also excluded because the browser sets its own multipart boundary.
  const isFormData = fetchOptions.body instanceof FormData;
  const hasJsonBody = fetchOptions.body != null && !isFormData;
  const headers: Record<string, string> = hasJsonBody
    ? { 'Content-Type': 'application/json' }
    : {};

  // Merge custom headers safely (without unsafe cast)
  if (fetchOptions.headers) {
    const incoming =
      fetchOptions.headers instanceof Headers
        ? Object.fromEntries(fetchOptions.headers.entries())
        : Array.isArray(fetchOptions.headers)
          ? Object.fromEntries(fetchOptions.headers)
          : fetchOptions.headers;
    Object.assign(headers, incoming);
  }

  if (!skipAuth && token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  // -----------------------------------------------------------------------
  // 401 Interceptor — auto-refresh + retry
  // -----------------------------------------------------------------------
  if (
    response.status === 401 &&
    !skipAuth &&
    !_isRetry &&
    !endpoint.startsWith('/auth/')
  ) {
    // If a refresh is already in-flight, queue this request
    if (isRefreshing) {
      const newToken = await new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
      // Retry the original request with the new token
      return apiClient<T>(endpoint, {
        ...options,
        _isRetry: true,
        headers: { ...headers, Authorization: `Bearer ${newToken}` },
      });
    }

    // Start the refresh flow
    isRefreshing = true;
    let refreshedToken: string;
    try {
      refreshedToken = await attemptTokenRefresh();
      drainQueue(null, refreshedToken);
    } catch (refreshError) {
      drainQueue(refreshError);
      throw new ApiClientError(401, { message: 'Sesion expirada' });
    } finally {
      isRefreshing = false;
    }

    // Retry the original request with the new token.
    // Deliberately OUTSIDE the try block so retry errors (e.g. 500, network)
    // bubble up naturally instead of being transformed into "Sesion expirada".
    return apiClient<T>(endpoint, {
      ...options,
      _isRetry: true,
      headers: { ...headers, Authorization: `Bearer ${refreshedToken}` },
    });
  }

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      const text = await response.text().catch(() => '');
      errorBody = {
        _rawText: text.slice(0, 500),
        _parseError: 'Response body was not valid JSON',
      };
    }
    throw new ApiClientError(response.status, errorBody);
  }

  // 204 No Content (e.g., DELETE endpoints) returns no body
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export class ApiClientError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly body: unknown,
  ) {
    super(`API Error: ${statusCode}`);
    this.name = 'ApiClientError';
  }
}
